import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Modal, Button, Typography } from 'antd';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Text } = Typography;

interface KeywordSelectModalProps {
  open: boolean;
  options: any[];
  onClose: () => void;
  onSelect: (textToInsert: string) => void;
  error?: boolean;
  loading?: boolean;
  spinning?: boolean;
  width?: number | string;
  destroyOnClose?: boolean;
  maskClosable?: boolean;
  modalRender?: (modal: React.ReactNode) => React.ReactNode;
  maskStyle?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  transitionName?: string;
  maskTransitionName?: string;
  previewRows?: number;
  /** Flutterフッターの高さ（px）。0の場合は非Flutter環境として扱う */
  flutterFooterHeight?: number;
  /** 重なり順（別モーダルの上に表示する場合などに指定）。既定: 1030 */
  zIndex?: number;
}

const MODAL_Z_OVERLAY = 1030;

const WRAP_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  // dvh / dvw でモバイルブラウザの URL バー伸縮にも追従させる
  width: '100dvw',
  height: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const resolveKeywordContent = (option: any): string => {
  if (typeof option === 'object') {
    return option.keywordcontent || option.keyword || '';
  }
  return String(option);
};

// ドラッグ可能なプレビュータグ
const SortablePreviewTag: React.FC<{
  id: string;
  content: string;
  onRemove: (content: string) => void;
}> = ({ id, content, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 10px',
    borderRadius: 20,
    border: '1.5px solid #595959',
    background: '#fff',
    color: '#595959',
    fontSize: 13,
    userSelect: 'none',
    touchAction: 'none',
    cursor: isDragging ? 'grabbing' : 'grab',
    flexShrink: 0,
  };

  return (
    <span ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {content}
      <span
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(content);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.12)',
          color: '#595959',
          fontSize: 11,
          lineHeight: 1,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        ✕
      </span>
    </span>
  );
};

const KeywordSelectModal: React.FC<KeywordSelectModalProps> = ({
  open,
  options,
  onClose,
  onSelect,
  error,
  width,
  destroyOnClose,
  maskClosable,
  modalRender,
  maskStyle,
  bodyStyle,
  transitionName,
  maskTransitionName,
  flutterFooterHeight = 0,
  zIndex = MODAL_Z_OVERLAY,
}) => {
  const [selectedContents, setSelectedContents] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleTagTap = (content: string) => {
    if (selectedContents.includes(content)) return;
    setSelectedContents((prev) => [...prev, content]);
  };

  const handleRemove = (content: string) => {
    setSelectedContents((prev) => prev.filter((c) => c !== content));
  };

  const handleClear = () => {
    setSelectedContents([]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSelectedContents((prev) => {
        const oldIndex = prev.indexOf(String(active.id));
        const newIndex = prev.indexOf(String(over.id));
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleClose = () => {
    setSelectedContents([]);
    onClose();
  };

  const handleSelect = () => {
    if (selectedContents.length > 0) {
      onSelect(selectedContents.join('') + '\n');
    }
    setSelectedContents([]);
    onClose();
  };

  return (
    <>
      {/* 背面を黒半透明で覆うオーバーレイ。
          他モーダル（ポータル描画）の上に確実に重ねるため document.body へポータルする。 */}
      {open &&
        ReactDOM.createPortal(
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex }} />,
          document.body
        )}
      <Modal
        title="キーワード入力"
        open={open}
        onCancel={handleClose}
        wrapStyle={{
          ...WRAP_STYLE,
          // Flutter フッター高 + iOS ホームインジケーター分を下部に確保（センタリングが自然に上がる）
          paddingBottom: `calc(${flutterFooterHeight}px + env(safe-area-inset-bottom, 0px))`,
          boxSizing: 'border-box',
        }}
        bodyStyle={{ padding: '16px 24px', ...bodyStyle }}
        styles={{
          content: {
            maxHeight: `calc(100dvh - ${flutterFooterHeight}px - env(safe-area-inset-bottom, 0px) - 40px)`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          body: { flex: 1, overflowY: 'auto', minHeight: 0 },
        }}
        width={width ?? '92vw'}
        destroyOnClose={destroyOnClose}
        maskClosable={maskClosable ?? false}
        mask={false}
        modalRender={modalRender}
        transitionName={transitionName}
        zIndex={zIndex}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          キャンセル
        </Button>,
        <Button key="select" type="primary" onClick={handleSelect}>
          選択
        </Button>,
      ]}
    >
      {/* プレビューエリア */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text strong>選択中</Text>
        {selectedContents.length > 0 && (
          <Button
            size="small"
            onClick={handleClear}
            style={{ background: '#8c8c8c', color: '#fff', border: 'none', borderRadius: 20 }}
          >
            クリア
          </Button>
        )}
      </div>
      <div
        style={{
          minHeight: 48,
          padding: '8px 10px',
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          background: '#fafafa',
          marginBottom: 20,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
        }}
      >
        {selectedContents.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>タップしたキーワードがここに並びます</Text>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={selectedContents} strategy={horizontalListSortingStrategy}>
              {selectedContents.map((content) => (
                <SortablePreviewTag
                  key={content}
                  id={content}
                  content={content}
                  onRemove={handleRemove}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* タグ選択エリア */}
      <div style={{ marginBottom: 8 }}>
        <Text strong>キーワード</Text>
      </div>
      {error ? (
        <div style={{
          padding: '12px',
          borderRadius: 6,
          background: '#fff2f0',
          border: '1px solid #ffccc7',
          color: '#a8071a',
          fontSize: 13,
        }}>
          キーワードの取得に失敗しました。
        </div>
      ) : options.length === 0 ? (
        <div style={{ color: '#8c8c8c', fontSize: 13, padding: '8px 0' }}>
          キーワードがありません。
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          maxHeight: '35dvh',
          overflowY: 'auto',
        }}
      >
        {options.map((option, index) => {
          const content = resolveKeywordContent(option);
          const selected = selectedContents.includes(content);
          return (
            <span
              key={index}
              onClick={() => handleTagTap(content)}
              style={{
                display: 'inline-block',
                padding: '6px 14px',
                borderRadius: 20,
                border: selected ? '1px solid #1a3461' : '1px solid #d9d9d9',
                background: selected ? '#1a3461' : '#fff',
                color: selected ? '#fff' : '#1a3461',
                fontSize: 14,
                cursor: selected ? 'default' : 'pointer',
                userSelect: 'none',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {content}
            </span>
          );
        })}
      </div>
      </Modal>
    </>
  );
};

export default KeywordSelectModal;
